/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getMe, LogOut, reset } from "../features/authSlice";
import { motion } from "framer-motion";
import { useStateContext } from "../contexts/ContextProvider";

import {
  FaLeaf,
  FaRecycle,
  FaUsers,
  FaTractor,
  FaStore,
  FaSeedling,
  FaHandshake,
  FaMapMarkedAlt,
  FaBox,
  FaExchangeAlt,
} from "react-icons/fa";
import { MdScience, MdEco } from "react-icons/md";
import { GiPlantSeed } from "react-icons/gi";

const Dashboard = () => {
  // State management
  const [jumlahProgram, setJumlahProgram] = useState(0);
  const [jumlahTeam, setJumlahTeam] = useState(0);
  const [jumlahProduk, setJumlahProduk] = useState(0);
  const [jumlahStories, setJumlahStories] = useState(0);
  const [redirectPath, setRedirectPath] = useState("");

  const [loading, setLoading] = useState(false);

  // Context
  const { currentMode, currentColor, getColorWithOpacity } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // Colors based on theme
  const isDark = currentMode === 'Dark';
  const accentColor = currentColor || '#c9862a';
  const bgColor = isDark ? '#1a1a18' : '#f7f3ec';
  const borderColor = isDark ? '#2e2e28' : '#e8e0d0';
  const textPrimary = isDark ? '#f0ece3' : '#1a1a18';
  const textSecondary = isDark ? '#a09880' : '#6b6456';
  const cardBg = isDark ? '#242420' : '#ffffff';

  // Data untuk cards dashboard (berbeda untuk siswa dan admin/guru)
  const getDashboardData = () => {
    return [
        {
          icon: <FaSeedling />,
          count: jumlahProgram,
          title: "Program Rumoh Pangan Aceh",
          onClick: () => navigate("/list-program")
        },
        {
          icon: <FaUsers />,
          count: jumlahTeam,
          title: "Team Rumoh Pangan Aceh",
          onClick: () => navigate("/list-team")
        },
        {
          icon: <FaBox />,
          count: jumlahProduk,
          title: "Produk Rumoh Pangan Aceh",
          onClick: () => navigate("/list-produkk")
        },
        {
          icon: <FaHandshake />,
          count: jumlahStories,
          title: "Cerita Rumoh Pangan Aceh",
          onClick: () => navigate("/list-stories")
        },
      ];
  };

  // Effects
  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      fetchDashboardData();
    } else {
      navigate("/");
    }
  }, [navigate, user]);

  // API Functions
  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const headers = { Authorization: `Bearer ${token}` };

      const [programRes, teamRes, produkRes, storiesRes] = await Promise.all([
        axios.get(`${apiUrl}/program`, { headers }),
        axios.get(`${apiUrl}/team`, { headers }),
        axios.get(`${apiUrl}/produk`, { headers }),
        axios.get(`${apiUrl}/stories`, { headers }),
      ]);

      setJumlahProgram(programRes.data.length);
      setJumlahTeam(teamRes.data.length);
      setJumlahProduk(produkRes.data.length);
      setJumlahStories(storiesRes.data.length);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf
            className="absolute top-20 left-10 text-8xl animate-pulse"
            style={{ color: accentColor }}
          />
          <FaRecycle
            className="absolute top-40 right-20 text-6xl animate-bounce"
            style={{ color: accentColor, animationDelay: '1s' }}
          />
          <GiPlantSeed
            className="absolute bottom-40 left-20 text-7xl animate-pulse"
            style={{ color: accentColor, animationDelay: '0.5s' }}
          />
          <MdEco
            className="absolute bottom-20 right-10 text-8xl animate-bounce"
            style={{ color: accentColor, animationDelay: '1.5s' }}
          />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div
            className="p-6 rounded-2xl shadow-lg border"
            style={{
              backgroundColor: cardBg,
              borderColor: borderColor,
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: accentColor }}
              >
                <MdEco className="text-white text-2xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                    Dashboard
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>—</span>
                  <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1 className="text-2xl font-serif font-bold mb-1" style={{ color: textPrimary }}>
                  Selamat Datang
                </h1>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {getDashboardData().map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="group cursor-pointer"
              onClick={item.onClick}
            >
              <div
                className="p-5 rounded-xl shadow-lg border transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="p-3 rounded-xl group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: accentColor }}
                  >
                    <span className="text-white text-xl">{item.icon}</span>
                  </div>
                  <div className="text-right">
                    <div
                      className="text-3xl font-bold font-serif"
                      style={{ color: accentColor }}
                    >
                      {loading && user?.role === "siswa" ? "..." : item.count}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1" style={{ color: textPrimary }}>
                    {item.title}
                  </h3>
                  <p className="text-xs" style={{ color: textSecondary }}>
                    {item.subtitle}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
          <div className="gap-8 mb-8">
            {/* Tentang Rumoh Pangan Aceh */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div
                className="p-6 rounded-xl shadow-lg border"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: accentColor }}
                  >
                    <FaLeaf className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-serif font-bold" style={{ color: textPrimary }}>
                      Tentang Rumoh Pangan Aceh
                    </h2>
                    <p className="text-xs" style={{ color: textSecondary }}>
                      Organisasi Penguatan Ketahanan Pangan Aceh
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div
                    className="p-4 rounded-xl border"
                    style={{
                      backgroundColor: isDark ? '#1a1a18' : `${accentColor}08`,
                      borderColor: borderColor
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="p-2 rounded-xl flex-shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        <FaUsers className="text-white text-sm" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: textPrimary }}>
                          Pemberdayaan Petani Lokal
                        </h3>
                        <p className="text-sm" style={{ color: textSecondary }}>
                          Membangun kapasitas dan memberdayakan petani lokal Aceh untuk meningkatkan produktivitas pertanian secara berkelanjutan.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="p-4 rounded-xl border"
                    style={{
                      backgroundColor: isDark ? '#1a1a18' : `${accentColor}08`,
                      borderColor: borderColor
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="p-2 rounded-xl flex-shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        <FaExchangeAlt className="text-white text-sm" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: textPrimary }}>
                          Distribusi Pangan Berkelanjutan
                        </h3>
                        <p className="text-sm" style={{ color: textSecondary }}>
                          Memastikan distribusi pangan yang merata dan berkelanjutan ke seluruh masyarakat Aceh melalui jaringan distribusi yang terintegrasi.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="p-4 rounded-xl border"
                    style={{
                      backgroundColor: isDark ? '#1a1a18' : `${accentColor}08`,
                      borderColor: borderColor
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="p-2 rounded-xl flex-shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        <GiPlantSeed className="text-white text-sm" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: textPrimary }}>
                          Ekosistem Pangan Tradisional
                        </h3>
                        <p className="text-sm" style={{ color: textSecondary }}>
                          Mengembangkan dan melestarikan ekosistem pangan tradisional Aceh untuk menjaga kearifan lokal dan keberagaman hayati.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>  
      </div>
    </div>
  );
};

export default Dashboard;
